import { makeStyles } from '@material-ui/styles'
import clsx from 'clsx'
import React, { forwardRef, useCallback } from 'react'
import useReactRouter from 'use-react-router'
import Text, { TextVariant } from 'core/elements/Text'
import Theme from 'core/themes/model'
import generateTestId from 'utils/test-helpers'
import FontAwesomeIcon from './FontAwesomeIcon'

type ISimpleLinkVariant = 'error' | 'primary' | 'secondary'

interface Props {
  src?: string
  staticContext?: any
  className?: string
  icon?: string
  variant?: ISimpleLinkVariant
  textVariant?: TextVariant
  onClick?: (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => void
  label?: string
  lineClamp?: number
  iconPosition?: 'left' | 'right'
  bold?: boolean
  children?: React.ReactNode
}

const useStyles = makeStyles<Theme>((theme) => ({
  root: {
    cursor: 'pointer',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  error: {
    color: theme.components.graph.error,
  },
  secondary: {
    color: theme.palette.secondary.main,
  },
  primary: {
    color: theme.palette.primary[900],
  },
  iconLeft: {
    fontSize: theme.typography.subtitle2.fontSize,
    marginRight: '8px',
    marginLeft: '0px',
  },
  iconRight: {
    fontSize: theme.typography.subtitle2.fontSize,
    marginRight: '0px',
    marginLeft: '8px',
  },
  bold: {
    fontWeight: 'bold',
  },
}))

// We need to destructure staticContext even though we are not using it in order to
// work around this issue: https://github.com/ReactTraining/react-router/issues/4683
// We need to use `forwardRef` as a workaround of an issue with material-ui Tooltip https://github.com/gregnb/mui-datatables/issues/595
const SimpleLink = forwardRef<HTMLAnchorElement, Props>(
  (
    {
      onClick,
      src,
      children,
      staticContext,
      className,
      icon,
      variant,
      textVariant = 'body2',
      label,
      lineClamp,
      iconPosition = 'right',
      bold = false,
      ...rest
    },
    ref,
  ) => {
    const classes = useStyles()
    const { history } = useReactRouter()
    const handleClick = useCallback(
      (e) => {
        // Prevent links inside of a table row from triggering row selection.
        e.stopPropagation()
        if (onClick) {
          e.preventDefault()
          onClick(e)
        }
        // If there is no provided onClick, just use the `src` as a normal link.
        if (src && !src.startsWith('http')) {
          // local paths should use the History's push state
          e.preventDefault()
          return history.push(src)
        }
        // Any path that starts with http should be treated as an external link
      },
      [src, history, onClick],
    )

    const colorClass =
      variant === 'error'
        ? classes.error
        : variant === 'secondary'
          ? classes.secondary
          : classes.primary

    return (
      <a
        className={clsx(className, classes.root, colorClass)}
        ref={ref}
        href={src || undefined}
        onClick={handleClick}
        data-testid={generateTestId(children)}
        {...rest}
      >
        {!!icon && iconPosition === 'left' && (
          <FontAwesomeIcon className={clsx(classes.iconLeft, 'icon')}>{icon}</FontAwesomeIcon>
        )}
        {textVariant ? (
          <Text
            variant={textVariant}
            lineClamp={lineClamp}
            component="span"
            className={clsx('simple-link-text', colorClass, bold && classes.bold)}
          >
            {children || src}
          </Text>
        ) : (
          children || src
        )}
        {!!icon && iconPosition === 'right' && (
          <FontAwesomeIcon className={clsx(classes.iconRight, 'icon')}>{icon}</FontAwesomeIcon>
        )}
      </a>
    )
  },
)

export default SimpleLink
